/**
 * 换我出镜参考视频时长按档(0924 车 D 一期止血,契约 §3.4)。
 *
 * ═══ 规矩(三仓同一张表)═══════════════════════════════════════════════════════
 *   ≤15s        任意档
 *   15 < t ≤30s  仅 2.5(`vapeur-seedance-2-5`)—— 其它档给「切 2.5 档并重新报价」一键切
 *   >30s        拒(上传即拦,不上传)
 * api `SWAP_MAX_DURATION_SEC` 按模型查表(超档 422 `SWAP_DURATION_EXCEEDS_TIER`),ai 提交前
 * ffprobe 只读兜底。档位上限单源 = `realPersonModelTiers.ts` 的 `maxDurationSec`
 * (2.5=30,其余=15),这里不另写第二份数字。
 *
 * ★存**原始**时长,不钳(0919 老板单:23.2s 参考视频被前端钳成 15 发出去,上游按真实
 *   23.2s 判超墙,用户看到的是通用桶「请稍后重试」)。钳位 = 对自己撒谎,档位不够就明说。
 * ★绝不截断:0915 对照单实证 ffmpeg 处理过的三件全被上游拒,手机原件两过 —— 所以
 *   这里只有「换档 / 重拍」两条出路,没有「帮你剪到 15 秒」。
 * ★容差 0.2s:上游 2.0 档的真实墙是 15.2s(0919 死因表),手机录的「15 秒」常是
 *   15.0x~15.1x,四舍五入到整数秒发 `durationSec` 仍在档内;超过容差才判超档。
 */
import {
  REAL_PERSON_MODEL_TIERS,
  realPersonModelTier,
  type RealPersonModelId,
} from "./realPersonModelTiers";

export const SWAP_DURATION_TOLERANCE_SEC = 0.2;

/** 唯一能拍到最长档的模型(档位表里 maxDurationSec 最大的那档)。 */
export const SWAP_LONG_TIER_MODEL: RealPersonModelId = REAL_PERSON_MODEL_TIERS.reduce((a, b) =>
  b.maxDurationSec > a.maxDurationSec ? b : a
).value;

/** 全档位里最长的上限(=30s)—— 超过它换什么档都不行。 */
export const SWAP_ABSOLUTE_MAX_SEC = realPersonModelTier(SWAP_LONG_TIER_MODEL).maxDurationSec;

export type SwapDurationVerdict =
  | { kind: "unknown" }
  | { kind: "ok"; rawSec: number }
  /** 当前档装不下,但切到 2.5 档就行 —— 给一键切。 */
  | { kind: "needs_long_tier"; rawSec: number; tierMaxSec: number }
  /** 超过全档位上限 —— 只能换一段更短的原件。 */
  | { kind: "over_max"; rawSec: number };

export function assessSwapDuration(
  rawSec: number | null | undefined,
  model: RealPersonModelId
): SwapDurationVerdict {
  if (rawSec == null || !Number.isFinite(rawSec) || rawSec <= 0) return { kind: "unknown" };
  if (rawSec > SWAP_ABSOLUTE_MAX_SEC + SWAP_DURATION_TOLERANCE_SEC) {
    return { kind: "over_max", rawSec };
  }
  const tierMaxSec = realPersonModelTier(model).maxDurationSec;
  if (rawSec > tierMaxSec + SWAP_DURATION_TOLERANCE_SEC) {
    return { kind: "needs_long_tier", rawSec, tierMaxSec };
  }
  return { kind: "ok", rawSec };
}

/** 判定是否拦提交(unknown = 还没测出来,由「还没上传」那条缺口提示负责,不在这里拦)。 */
export function swapVerdictBlocksSubmit(v: SwapDurationVerdict): boolean {
  return v.kind === "needs_long_tier" || v.kind === "over_max";
}

/**
 * 提交/报价用的整数秒:原始时长四舍五入(不钳到档位上限 —— 超档由 verdict 拦,
 * 不许静默改小;api 按真实值再判一次)。
 */
export function swapPayloadDurationSec(rawSec: number): number {
  return Math.round(rawSec);
}

/** 人话(页面、测试、缺口提示同一份)。 */
export function swapVerdictText(v: SwapDurationVerdict, modelLabel: string): string | null {
  switch (v.kind) {
    case "needs_long_tier":
      return `参考视频 ${v.rawSec.toFixed(1)} 秒,「${modelLabel}」档最长 ${v.tierMaxSec} 秒。切到「旗舰」档可以拍到 ${SWAP_ABSOLUTE_MAX_SEC} 秒(会按「旗舰」档重新报价)。`;
    case "over_max":
      return `参考视频 ${v.rawSec.toFixed(1)} 秒,换我出镜最长 ${SWAP_ABSOLUTE_MAX_SEC} 秒(「旗舰」档)。请用手机重新拍一段 ${SWAP_ABSOLUTE_MAX_SEC} 秒以内的原件 —— 别剪辑、别转码,处理过的视频基本都会被拒。`;
    default:
      return null;
  }
}
